import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AuthCerts.css';
import { useTranslation } from 'react-i18next';
import { Button, Jumbotron, ListGroup, Navbar } from 'react-bootstrap';
import ToastComponent from '../Toast/Toast';
import { Redirect } from "react-router-dom";
import ThemeService from '../../services/ThemeService';
import Utils from '../../services/Utils';
import SigningProcessModal from '../Modals/SigningProcessModal';
import LoadingCircle from '../LoadingCircle';
import { generateKeyVaultSendOTPUri, postPrivSignature, generateFirmaRemotaOTPUri, generateFirmaRemotaSessionUri, putManagementSignature, putPrivSignature, generateKeyVaultOpenSessionUri, generateSwisscomSignHashUri, generateSwisscomCompleteSignatureHashUri } from '../../services/signatureMethods/customServiceSignature';
import ProfileService from '../../services/Profile/ProfileService';
import CertificateService from '../../services/Certificate/CertificateService';
import * as oauthService from '../../services/oauth/oauthService';
import KeyVaultSignature from '../KeyVaultSignature/KeyVaultSignature.component';
import SwisscomSignature from '../SwisscomSignature/SwisscomSignature.component';
import FirmaRemotaSignature from '../FirmaRemota/FirmaRemota.component';

const AuthCerts = ({ location }) => {
    const { t } = useTranslation();

    // Values already persisted in sessionStorage/localStorage — no state hook needed
    const oauthTokenB64 = sessionStorage.getItem('oauthTokenB64');
    const user = sessionStorage.getItem('user');
    const clientId = sessionStorage.getItem('clientId');
    const redirectUri = sessionStorage.getItem('redirectUri');
    const sessionState = sessionStorage.getItem('state');
    const hash = sessionStorage.getItem('hash');
    const hashalgo = sessionStorage.getItem('hashalgo');
    const language = localStorage.getItem('i18nextLng');

    // Dynamic UI / process state
    const [time, setTime] = useState(0);
    const [loading, setLoading] = useState(false);
    const [certificatesList, setCertificatesList] = useState(null);
    const [showKeyVaultModal, setShowKeyVaultModal] = useState(false);
    const [showFirmaRemotaModal, setShowFirmaRemotaModal] = useState(false);
    const [certificateSelected, setCertificateSelected] = useState({});
    const [sessionExpired, setSessionExpired] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorData, setErrorData] = useState('');
    const [errorType, setErrorType] = useState('');
    const [redirect, setRedirect] = useState(false);
    const [sessionOpened, setSessionOpened] = useState(false);
    const [loadingSignatureProcess, setLoadingSignatureProcess] = useState(false);
    const [showSwisscomModal, setShowSwisscomModal] = useState(false);
    const [showSwisscomConfirmationModal, setShowSwisscomConfirmationModal] = useState(false);
    const [swisscomRedirectURL, setSwisscomRedirectURL] = useState('');
    const [swisscomRequestID, setSwisscomRequestID] = useState('');
    const [loadingSwisscomCheck, setLoadingSwisscomCheck] = useState(false);
    const [swisscomError, setSwisscomError] = useState('');
    const [loadingSwisscomRequest, setLoadingSwisscomRequest] = useState(false);

    // Ref to hold the interval ID for the session-expired timer
    const timerRef = useRef(null);

    // ---------------------------------------------------------------------------
    // Timer helpers
    // ---------------------------------------------------------------------------

    const startTimer = useCallback(() => {
        timerRef.current = setInterval(() => setTime(prev => prev + 1), 60000);
    }, []);

    const stopTimer = useCallback(() => {
        clearInterval(timerRef.current);
    }, []);

    // ---------------------------------------------------------------------------
    // Error helpers
    // ---------------------------------------------------------------------------

    const handleSessionExpiredError = useCallback((status) => {
        if (status === 401) {
            setSessionExpired(true);
            startTimer();
        }
    }, [startTimer]);

    const handleGenericError = useCallback((error) => {
        handleSessionExpiredError(error.response.status);
        if (error.response.status !== 401) {
            setErrorType(error.response.status);
            setErrorData(error.response.data);
            setShowError(true);
        }
    }, [handleSessionExpiredError]);

    // ---------------------------------------------------------------------------
    // Storage
    // ---------------------------------------------------------------------------

    const cleanStorage = useCallback(() => {
        sessionStorage.clear();
    }, []);

    // ---------------------------------------------------------------------------
    // OTP helpers
    // ---------------------------------------------------------------------------

    const getViDMobileUserGUIDFromOauthToken = useCallback(() => {
        const tokenParts = oauthTokenB64.split('.');
        const jsonTokenInfo = atob(tokenParts[1]);
        const tokenInfo = JSON.parse(jsonTokenInfo);
        return tokenInfo.sub;
    }, [oauthTokenB64]);

    const firmaRemotaSendOTP = useCallback(() => {
        const ViDMobileUserGUID = getViDMobileUserGUIDFromOauthToken();
        putPrivSignature(generateFirmaRemotaOTPUri(ViDMobileUserGUID), '', oauthTokenB64)
            .catch((error) => {
                handleGenericError(error);
            });
    }, [getViDMobileUserGUIDFromOauthToken, oauthTokenB64, handleGenericError]);

    const keyVaultSendOTP = useCallback((certificate) => {
        postPrivSignature(generateKeyVaultSendOTPUri(certificate.GUID), '', oauthTokenB64)
            .catch((error) => {
                handleGenericError(error);
            });
    }, [oauthTokenB64, handleGenericError]);

    // ---------------------------------------------------------------------------
    // Certificate selection
    // ---------------------------------------------------------------------------

    const handleCertSelection = useCallback((certificate) => {
        let showKVModal = false;
        let showFRModal = false;
        let showSCModal = false;

        if (certificate.CertType === 'KeyVault' && certificate.AuthenticationType === 'PIN') {
            showKVModal = true;
        }
        if (certificate.CertType === 'KeyVault' && certificate.AuthenticationType === 'SMS') {
            showKVModal = true;
            keyVaultSendOTP(certificate);
        }
        if (certificate.CertType === 'FirmaRemota' && certificate.AuthenticationType === 'APP') {
            showFRModal = true;
        }
        if (certificate.CertType === 'FirmaRemota' && certificate.AuthenticationType === 'SMS') {
            showFRModal = true;
            firmaRemotaSendOTP();
        }
        if (certificate.CertType === 'Swisscom') {
            showSCModal = true;
        }

        setShowKeyVaultModal(showKVModal);
        setShowFirmaRemotaModal(showFRModal);
        setCertificateSelected(certificate);
        setShowSwisscomModal(showSCModal);
    }, [keyVaultSendOTP, firmaRemotaSendOTP]);

    // ---------------------------------------------------------------------------
    // Modal close
    // ---------------------------------------------------------------------------

    const handleClose = useCallback(() => {
        setShowKeyVaultModal(false);
        setShowFirmaRemotaModal(false);
        setLoadingSignatureProcess(false);
        setShowSwisscomModal(false);
        setShowSwisscomConfirmationModal(false);
        setLoadingSwisscomCheck(false);
        setLoadingSwisscomRequest(false);
    }, []);

    // ---------------------------------------------------------------------------
    // OAuth code helpers
    // ---------------------------------------------------------------------------

    const getOauthViDMobileCode = useCallback((certSelected) => {
        const currentScope = certSelected.CertType === 'KeyVault'
            ? 'vidmobilecert\\keyvault'
            : 'vidmobilecert\\firmaremota';
        const authorizationHeader = btoa(clientId + ':');

        const codeBody = {
            username: user + '\\' + certSelected.GUID,
            password: location.state.password,
            grant_type: 'authorization_code',
            scope: currentScope
        };

        return oauthService.getOauthCode(codeBody, authorizationHeader)
            .catch((error) => {
                if (error.response) {
                    setErrorType(error.response.status);
                    setErrorData(error.response.data);
                    setShowError(true);
                    setShowKeyVaultModal(false);
                    setShowFirmaRemotaModal(false);
                    setLoadingSignatureProcess(false);
                }
                throw error;
            });
    }, [clientId, user, location]);

    const getOauthViDMobileCodeSwisscom = useCallback((requestID) => {
        const authorizationHeader = btoa(clientId + ':');

        const codeBody = {
            username: user + '\\' + requestID,
            password: location.state.password,
            grant_type: 'authorization_code',
            scope: 'vidmobilecert\\swisscom'
        };

        return oauthService.getOauthCode(codeBody, authorizationHeader)
            .catch((error) => {
                if (error.response) {
                    setErrorType(error.response.status);
                    setErrorData(error.response.data);
                    setShowError(true);
                    setShowKeyVaultModal(false);
                    setShowFirmaRemotaModal(false);
                    setLoadingSignatureProcess(false);
                }
                throw error;
            });
    }, [clientId, user, location]);

    // ---------------------------------------------------------------------------
    // Signature session openers
    // ---------------------------------------------------------------------------

    const firmaRemotaOpenSession = useCallback((_, __, ___, certificatePin) => {
        setLoadingSignatureProcess(true);
        setShowFirmaRemotaModal(false);

        const dataJson = { OTP: certificatePin };

        getOauthViDMobileCode(certificateSelected)
            .then((res) => {
                if (!res) return;
                const accessToken = res.data.access_token;
                putManagementSignature(generateFirmaRemotaSessionUri(), dataJson, oauthTokenB64)
                    .then(() => {
                        setSessionOpened(true);
                        setLoadingSignatureProcess(false);
                        const redirectURL = `${redirectUri}?code=${accessToken}&state=${sessionState}&certguid=${certificateSelected.GUID}`;
                        setTimeout(() => { window.location.href = redirectURL; }, 3000);
                        cleanStorage();
                    })
                    .catch((error) => {
                        if (error.response) {
                            setErrorType(error.response.status);
                            setErrorData(error.response.data);
                        } else {
                            setErrorType('Undefined');
                            setErrorData('Network error');
                        }
                        setShowError(true);
                        setLoadingSignatureProcess(false);
                    });
            })
            .catch((error) => {
                if (error.response) {
                    setErrorType(error.response.status);
                    setErrorData(error.response.data);
                } else {
                    setErrorType('Undefined');
                    setErrorData('Network error');
                }
                setShowError(true);
                setLoadingSignatureProcess(false);
            });
    }, [certificateSelected, oauthTokenB64, redirectUri, sessionState, getOauthViDMobileCode, cleanStorage]);

    const keyVaultOpenSession = useCallback((_, __, ___, certificatePin) => {
        setLoadingSignatureProcess(true);
        setShowKeyVaultModal(false);

        getOauthViDMobileCode(certificateSelected)
            .then((res) => {
                if (!res) return;
                const dataJson = { OTP: certificatePin };
                const accessToken = res.data.access_token;
                putManagementSignature(generateKeyVaultOpenSessionUri(certificateSelected.GUID), dataJson, oauthTokenB64)
                    .then(() => {
                        setSessionOpened(true);
                        setLoadingSignatureProcess(false);
                        const redirectURL = `${redirectUri}?code=${accessToken}&state=${sessionState}&certguid=${certificateSelected.GUID}`;
                        setTimeout(() => { window.location.href = redirectURL; }, 3000);
                        cleanStorage();
                    })
                    .catch((error) => {
                        if (error.response) {
                            setErrorType(error.response.status);
                            setErrorData(error.response.data);
                        } else {
                            setErrorType('Undefined');
                            setErrorData('Network error');
                        }
                        setShowError(true);
                        setLoadingSignatureProcess(false);
                    });
            })
            .catch((error) => {
                if (error.response) {
                    setErrorType(error.response.status);
                    setErrorData(error.response.data);
                } else {
                    setErrorType('Undefined');
                    setErrorData('Network error');
                }
                setShowError(true);
                setLoadingSignatureProcess(false);
            });
    }, [certificateSelected, oauthTokenB64, redirectUri, sessionState, getOauthViDMobileCode, cleanStorage]);

    // ---------------------------------------------------------------------------
    // Swisscom flow
    // ---------------------------------------------------------------------------

    const signDocSwisscom = useCallback(() => {
        const data = { Hash: hash, HashAlgorithm: hashalgo };

        postPrivSignature(generateSwisscomSignHashUri(), data, oauthTokenB64)
            .then((res) => {
                const swisscomReturnObj = res.data;
                const isUrlDefined = Boolean(swisscomReturnObj.RedirectURL);
                if (isUrlDefined) {
                    Utils.openTab(swisscomReturnObj.RedirectURL);
                }
                setSwisscomRedirectURL(swisscomReturnObj.RedirectURL);
                setSwisscomRequestID(swisscomReturnObj.RequestID);
                setLoadingSwisscomRequest(false);
            })
            .catch((error) => {
                if (error.response) {
                    if (error.response.status === 401) {
                        setSessionExpired(true);
                        startTimer();
                    } else {
                        setErrorType(error.response.status);
                        setErrorData(error.response.data);
                        setShowError(true);
                        setShowSwisscomConfirmationModal(false);
                        setShowSwisscomModal(false);
                        setLoadingSwisscomCheck(false);
                        setLoadingSwisscomRequest(false);
                    }
                } else {
                    setErrorType('Undefined');
                    setErrorData('Network error');
                    setShowError(true);
                    setShowSwisscomConfirmationModal(false);
                    setShowSwisscomModal(false);
                    setLoadingSwisscomCheck(false);
                }
            });
    }, [hash, hashalgo, oauthTokenB64, startTimer]);

    // swisscomRequestID is passed as a parameter to avoid a stale-closure race condition
    // between setSwisscomRequestID (called in signDocSwisscom) and this callback.
    const completeSwisscomSignature = useCallback((requestID) => {
        postPrivSignature(generateSwisscomCompleteSignatureHashUri(requestID), '', oauthTokenB64)
            .then(() => {
                getOauthViDMobileCodeSwisscom(requestID)
                    .then((res) => {
                        if (!res) return;
                        const accessToken = res.data.access_token;
                        setLoadingSwisscomCheck(false);
                        setShowSwisscomConfirmationModal(false);
                        const redirectURL = `${redirectUri}?code=${accessToken}&state=${sessionState}&requestid=${requestID}`;
                        setTimeout(() => { window.location.href = redirectURL; }, 3000);
                        cleanStorage();
                    })
                    .catch((error) => {
                        if (error.response) {
                            setErrorType(error.response.status);
                            setErrorData(error.response.data);
                        } else {
                            setErrorType('Undefined');
                            setErrorData('Network error');
                        }
                        setShowError(true);
                        setShowSwisscomConfirmationModal(false);
                        setShowSwisscomModal(false);
                        setLoadingSwisscomCheck(false);
                    });
            })
            .catch((error) => {
                if (error.response) {
                    if (error.response.status === 401) {
                        setSessionExpired(true);
                        startTimer();
                    } else if (error.response.status === 409) {
                        setLoadingSwisscomCheck(false);
                        setSwisscomError(t('documents.swisscom-error'));
                    } else {
                        setErrorType(error.response.status);
                        setErrorData(error.response.data);
                        setShowError(true);
                        setShowSwisscomConfirmationModal(false);
                        setShowSwisscomModal(false);
                        setLoadingSwisscomCheck(false);
                    }
                } else {
                    setErrorType('Undefined');
                    setErrorData('Network error');
                    setShowError(true);
                    setShowSwisscomConfirmationModal(false);
                    setShowSwisscomModal(false);
                    setLoadingSwisscomCheck(false);
                }
            });
    }, [oauthTokenB64, redirectUri, sessionState, getOauthViDMobileCodeSwisscom, cleanStorage, startTimer, t]);

    // ---------------------------------------------------------------------------
    // Session logout
    // ---------------------------------------------------------------------------

    const doLogOut = useCallback(() => {
        stopTimer();
        setRedirect(true);
    }, [stopTimer]);

    // ---------------------------------------------------------------------------
    // Certificate list loading
    // ---------------------------------------------------------------------------

    const getUserCertificates = useCallback((currentUserData, currentUserCertificatesAuth) => {
        return CertificateService.getUserCertificates(currentUserData.legalid)
            .then((res) => res.data)
            .catch((error) => {
                if (error.response && error.response.status === 401) {
                    setSessionExpired(true);
                    setLoading(false);
                    startTimer();
                }
                if (error.response && error.response.status === 404) {
                    if (!currentUserCertificatesAuth || (!currentUserCertificatesAuth.Swisscom && !currentUserCertificatesAuth.Autofirma)) {
                        setCertificatesList(<p>{t('documents.cert-type-selection-empty')}</p>);
                        return [];
                    }
                    if (currentUserCertificatesAuth.Swisscom || currentUserCertificatesAuth.Autofirma) {
                        return [];
                    }
                }
                if (error.response) {
                    setErrorType(error.response.status);
                    setErrorData(error.response.data);
                    setShowError(true);
                    setLoading(false);
                }
                throw error;
            });
    }, [startTimer, t]);

    const loadViDMobileUserCertificateTypes = useCallback(() => {
        return CertificateService.getCertificateTypes()
            .then((res) => res.data)
            .catch((error) => {
                if (error.response && error.response.status === 401) {
                    setSessionExpired(true);
                    setLoading(false);
                    startTimer();
                } else if (error.response) {
                    setErrorType(error.response.status);
                    setErrorData(error.response.data);
                    setShowError(true);
                    setLoading(false);
                }
                throw error;
            });
    }, [startTimer]);

    const loadUserData = useCallback(() => {
        return ProfileService.getUserData()
            .then((res) => res.data)
            .catch((error) => {
                if (error.response && error.response.status === 401) {
                    setSessionExpired(true);
                    setLoading(false);
                    startTimer();
                } else if (error.response) {
                    setErrorType(error.response.status);
                    setErrorData(error.response.data);
                    setShowError(true);
                    setLoading(false);
                }
                throw error;
            });
    }, [startTimer]);

    const loadViDMobileUserCertificateList = useCallback(() => {
        setLoading(true);

        loadUserData()
            .then((fetchedUserData) => {
                if (!fetchedUserData) {
                    setErrorType('Exception');
                    setErrorData(t('general.no-user-data'));
                    setShowError(true);
                    setLoading(false);
                    return;
                }

                loadViDMobileUserCertificateTypes()
                    .then((fetchedCertTypes) => {

                        getUserCertificates(fetchedUserData, fetchedCertTypes)
                            .then((fetchedCerts) => {
                                let ViDMobileUserAllowFirmaRemoraCerts = false;
                                let ViDMobileUserAllowKeyVaultCerts = false;
                                let ViDMobileUserAllowSwisscomCerts = false;

                                if (fetchedCertTypes) {
                                    ViDMobileUserAllowFirmaRemoraCerts = fetchedCertTypes.FirmaRemota;
                                    ViDMobileUserAllowKeyVaultCerts = fetchedCertTypes.KeyVault;
                                    ViDMobileUserAllowSwisscomCerts = fetchedCertTypes.Swisscom;
                                }

                                const userCertsFiltered = (fetchedCerts || []).reduce((allowed, certificate) => {
                                    if (
                                        (certificate.CertType === 'FirmaRemota' && ViDMobileUserAllowFirmaRemoraCerts) ||
                                        (certificate.CertType === 'KeyVault' && ViDMobileUserAllowKeyVaultCerts)
                                    ) {
                                        allowed.push(certificate);
                                    }
                                    return allowed;
                                }, []);

                                if (ViDMobileUserAllowSwisscomCerts) {
                                    userCertsFiltered.push({
                                        CertType: 'Swisscom',
                                        ExpirationDate: '-',
                                        certalias: 'Swisscom'
                                    });
                                }

                                const builtCertificatesList = userCertsFiltered.map((certificate) => {
                                    if (certificate.certalias !== 'Swisscom') {
                                        return (
                                            <ListGroup.Item
                                                key={certificate.GUID + certificate.certalias}
                                                action
                                                variant="light"
                                                onClick={() => handleCertSelection(certificate)}
                                            >
                                                <ul style={{ listStyleType: 'none' }}>
                                                    <li><b>{t('general.alias')}</b>: {certificate.certalias}</li>
                                                    <li><b>{t('general.type')}</b>: {certificate.CertType}</li>
                                                    <li><b>{t('general.expiration-date')}</b>: {certificate.ExpirationDate}</li>
                                                </ul>
                                            </ListGroup.Item>
                                        );
                                    }
                                    return (
                                        <ListGroup.Item
                                            key={certificate.GUID + certificate.certalias}
                                            action
                                            variant="light"
                                            onClick={() => handleCertSelection(certificate)}
                                        >
                                            <ul style={{ listStyleType: 'none' }} className="list-container-2">
                                                <li>{certificate.certalias}</li>
                                            </ul>
                                        </ListGroup.Item>
                                    );
                                });

                                setCertificatesList(builtCertificatesList);
                                setLoading(false);
                            })
                            .catch((error) => {
                                setErrorType(error.response.status);
                                setErrorData(error.response.data);
                                setShowError(true);
                                setLoading(false);
                            });
                    })
                    .catch((error) => {
                        setErrorType(error.response.status);
                        setErrorData(error.response.data);
                        setShowError(true);
                        setLoading(false);
                    });
            })
            .catch((error) => {
                setErrorType(error.response.status);
                setErrorData(error.response.data);
                setShowError(true);
                setLoading(false);
            });
    }, [loadUserData, loadViDMobileUserCertificateTypes, getUserCertificates, handleCertSelection, t]);

    // ---------------------------------------------------------------------------
    // Lifecycle
    // ---------------------------------------------------------------------------

    useEffect(() => {
        ThemeService.setTheme();
        sessionStorage.setItem('authCerts', 'true');
        loadViDMobileUserCertificateList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    const timeText = language === 'es'
        ? `Hace ${time} minutos`
        : language === 'en'
            ? `${time} minutes ago`
            : language === 'ca'
                ? `Fa ${time} minuts`
                : `Hace ${time} minutos`;

    if (redirect) {
        return <Redirect to={{ pathname: '/' }} />;
    }

    // Build the state-like object SwisscomSignature expects via parentState prop
    const swisscomParentState = {
        showSwisscomModal,
        showSwisscomConfirmationModal,
        swisscomRedirectURL,
        swisscomRequestID,
        loadingSwisscomCheck,
        swisscomError,
        loadingSwisscomRequest,
        certificateSelected,
        oauthTokenB64
    };

    return (
        <div>
            <header className="App-header">
                <Navbar collapseOnSelect expand="lg" bg="light" variant="dark">
                    <Navbar.Brand>
                        <img
                            src={require('../../assets/images/logo-vid-sit.png')}
                            height="35"
                            width="auto"
                            alt="Validated ID logo"
                        />
                    </Navbar.Brand>
                </Navbar>
            </header>

            {sessionOpened && (
                <ToastComponent
                    onClose={() => setSessionOpened(false)}
                    show={sessionOpened}
                    autohide
                    delay={3000}
                    title={t('general.exit')}
                    message={t('general.exit-text')}
                    variant="info"
                    zIndex={10}
                />
            )}

            {showError && (
                <ToastComponent
                    onClose={() => setShowError(false)}
                    show={showError}
                    title={<>{t('general.error')} {errorType}</>}
                    message={errorData}
                    variant="error"
                    closeButton
                    zIndex={11}
                />
            )}

            {sessionExpired && (
                <ToastComponent
                    show={sessionExpired}
                    title={t('general.notification')}
                    subtitle={timeText}
                    variant="session-expired"
                    zIndex={10}
                    closeButton={false}
                >
                    <p>{t('general.session-expired')}</p>
                    <Button
                        type="button"
                        className="btn btn-primary"
                        id="sign-all-docs"
                        onClick={doLogOut}
                    >
                        {t('general.accept')}
                    </Button>
                </ToastComponent>
            )}

            <Jumbotron>
                <h1 style={{ paddingTop: '2rem', paddingBottom: '0.5rem' }}>
                    {t('certificates.certificate-selection')}
                </h1>
                <p style={{ paddingBottom: '2rem', paddingTop: '0.5rem' }}>
                    {t('certificates.certificate-selection-text')}
                </p>
            </Jumbotron>

            {loading && <LoadingCircle centerVertically={true} />}

            {!loading && (
                <div className="certificates-auth-container">
                    {certificatesList}

                    <SigningProcessModal
                        showModal={loadingSignatureProcess}
                        showProcess={false}
                    />

                    <KeyVaultSignature
                        show={showKeyVaultModal}
                        onClose={handleClose}
                        onSuccess={keyVaultOpenSession}
                        certificateSelected={certificateSelected}
                    />

                    <FirmaRemotaSignature
                        show={showFirmaRemotaModal}
                        onClose={handleClose}
                        onSuccess={firmaRemotaOpenSession}
                        certificateSelected={certificateSelected}
                    />

                    <SwisscomSignature
                        parentState={swisscomParentState}
                        onClose={handleClose}
                        onSuccessSignDocSelection={() => {
                            setShowSwisscomModal(false);
                            setShowSwisscomConfirmationModal(true);
                            setLoadingSwisscomRequest(true);
                            signDocSwisscom();
                        }}
                        onSuccess={(currentRequestID) => {
                            setLoadingSwisscomCheck(true);
                            setSwisscomError('');
                            completeSwisscomSignature(currentRequestID ?? swisscomRequestID);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default AuthCerts;
